import { useState, useRef, useEffect } from 'react';

const areas = [
  {
    icon: 'ri-first-aid-kit-line',
    title: 'Acidente de Trabalho',
    desc: 'Representação completa em casos de acidentes ocorridos no ambiente de trabalho ou no trajeto. Indenização por danos morais, materiais e estéticos.',
    image: 'https://readdy.ai/api/search-image?query=construction%20worker%20safety%20helmet%20industrial%20workplace%20accident%20prevention%20professional%20photography%20dark%20moody%20background%20dramatic%20lighting%20high%20contrast%20black%20and%20gold%20tones%20cinematic%20style%20labor%20rights%20concept%20serious%20atmosphere&width=600&height=750&seq=area1&orientation=portrait',
  },
  {
    icon: 'ri-lungs-line',
    title: 'Doença Ocupacional',
    desc: 'Doenças causadas ou agravadas pelas condições de trabalho. Reconhecimento do nexo causal e garantia de todos os direitos previdenciários.',
    image: 'https://readdy.ai/api/search-image?query=medical%20professional%20examining%20patient%20occupational%20health%20clinic%20dark%20sophisticated%20interior%20warm%20golden%20light%20professional%20healthcare%20environment%20serious%20atmosphere%20high%20contrast%20moody%20photography%20black%20background&width=600&height=750&seq=area2&orientation=portrait',
  },
  {
    icon: 'ri-mental-health-line',
    title: 'Burnout e Depressão',
    desc: 'Esgotamento profissional e transtornos psicológicos causados pelo trabalho. Reconhecimento como doença ocupacional e ação indenizatória.',
    image: 'https://readdy.ai/api/search-image?query=stressed%20exhausted%20worker%20at%20desk%20burnout%20concept%20dark%20moody%20office%20environment%20dramatic%20shadows%20warm%20golden%20accent%20light%20cinematic%20photography%20mental%20health%20workplace%20serious%20professional%20atmosphere&width=600&height=750&seq=area3&orientation=portrait',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Dores/lesões por esforço no trabalho',
    desc: 'Lesões por Esforço Repetitivo e Distúrbios Osteomusculares Relacionados ao Trabalho. Síndrome do túnel do carpo, tendinite e problemas de coluna.',
    image: 'https://readdy.ai/api/search-image?query=worker%20hands%20repetitive%20strain%20injury%20wrist%20pain%20ergonomic%20workplace%20dark%20background%20dramatic%20lighting%20golden%20warm%20tones%20professional%20photography%20labor%20rights%20concept%20high%20contrast%20cinematic&width=600&height=750&seq=area4&orientation=portrait',
  },
  {
    icon: 'ri-shield-user-line',
    title: 'Reintegração e Estabilidade',
    desc: 'Proteção contra demissão indevida durante período de afastamento. Garantia de estabilidade e reintegração ao emprego quando necessário.',
    image: 'https://readdy.ai/api/search-image?query=professional%20lawyer%20office%20meeting%20client%20consultation%20dark%20sophisticated%20interior%20warm%20golden%20desk%20lamp%20legal%20documents%20serious%20professional%20atmosphere%20high%20contrast%20moody%20photography%20black%20and%20gold%20tones&width=600&height=750&seq=area5&orientation=portrait',
  },
];

const VISIBLE_DESKTOP = 3;

function AreaCard({ area, active }: { area: typeof areas[0]; active: boolean }) {
  return (
    <div
      className={`relative rounded-3xl overflow-hidden flex-shrink-0 transition-all duration-500 cursor-default group ${
        active ? 'opacity-100 scale-100' : 'opacity-60 scale-95'
      }`}
      style={{ width: '320px', height: '420px' }}
    >
      <img
        src={area.image}
        alt={area.title}
        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-black/90 via-brand-black/40 to-transparent group-hover:from-brand-black/95 transition-all duration-500" />
      <div className="absolute bottom-0 left-0 right-0 p-7">
        <div className="w-10 h-10 flex items-center justify-center rounded-full border border-brand-gold/60 mb-4">
          <i className={`${area.icon} text-brand-gold text-lg`} />
        </div>
        <h3 className="font-sans text-white text-xl font-bold mb-2">{area.title}</h3>
        <p className="font-sans text-white/70 text-sm leading-relaxed opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-400">
          {area.desc}
        </p>
      </div>
    </div>
  );
}

export default function AreasSection() {
  const [current, setCurrent] = useState(0);
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.2 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  const maxIndex = areas.length - VISIBLE_DESKTOP;

  const prev = () => setCurrent((c) => Math.max(0, c - 1));
  const next = () => setCurrent((c) => Math.min(maxIndex, c + 1));

  const mobilePrev = () => setCurrent((c) => Math.max(0, c - 1));
  const mobileNext = () => setCurrent((c) => Math.min(areas.length - 1, c + 1));

  return (
    <section id="areas" className="bg-brand-light py-20 md:py-28 px-4 md:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Title */}
        <div
          ref={titleRef}
          className="text-center mb-14"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="text-brand-gold font-sans text-xs font-semibold tracking-widest uppercase">Especialidades</span>
          <h2 className="font-serif text-brand-black text-3xl md:text-5xl font-bold mt-3 leading-tight">
            Nossas Áreas de Atuação
          </h2>
          <p className="font-sans text-brand-green/80 text-base md:text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
            Atuamos com profundidade e estratégia nas principais causas de acidente de trabalho e doença ocupacional em São Paulo.
          </p>
        </div>

        {/* Desktop Carousel */}
        <div className="hidden md:block relative">
          <div className="overflow-hidden">
            <div
              className="flex gap-6 transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(calc(-${current * (320 + 24)}px))` }}
            >
              {areas.map((area, i) => (
                <AreaCard key={i} area={area} active={i >= current && i < current + VISIBLE_DESKTOP} />
              ))}
            </div>
          </div>

          {/* Arrows */}
          <button
            onClick={prev}
            disabled={current === 0}
            className={`absolute -left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full border-2 border-brand-gold text-brand-gold bg-white hover:bg-brand-gold hover:text-white transition-all duration-300 cursor-pointer ${current === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            onClick={next}
            disabled={current >= maxIndex}
            className={`absolute -right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full border-2 border-brand-gold text-brand-gold bg-white hover:bg-brand-gold hover:text-white transition-all duration-300 cursor-pointer ${current >= maxIndex ? 'opacity-30 cursor-not-allowed' : ''}`}
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`rounded-full transition-all duration-300 cursor-pointer ${i === current ? 'w-6 h-2 bg-brand-gold' : 'w-2 h-2 bg-brand-gold/30 hover:bg-brand-gold/60'}`}
              />
            ))}
          </div>
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(calc(-${current * 100}%))` }}
            >
              {areas.map((area, i) => (
                <div
                  key={i}
                  className="relative rounded-3xl overflow-hidden flex-shrink-0 w-full group"
                  style={{ height: '400px' }}
                >
                  <img src={area.image} alt={area.title} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-black/90 via-brand-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full border border-brand-gold/60 mb-3">
                      <i className={`${area.icon} text-brand-gold text-base`} />
                    </div>
                    <h3 className="font-sans text-white text-lg font-bold mb-2">{area.title}</h3>
                    <p className="font-sans text-white/70 text-xs leading-relaxed">{area.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Arrows + Dots */}
          <div className="flex items-center justify-between mt-6 px-2">
            <button
              onClick={mobilePrev}
              disabled={current === 0}
              className={`w-11 h-11 flex items-center justify-center rounded-full border-2 border-brand-gold text-brand-gold bg-white transition-all duration-300 cursor-pointer ${current === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>

            <div className="flex gap-2">
              {areas.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${i === current ? 'w-6 h-2 bg-brand-gold' : 'w-2 h-2 bg-brand-gold/30'}`}
                />
              ))}
            </div>

            <button
              onClick={mobileNext}
              disabled={current === areas.length - 1}
              className={`w-11 h-11 flex items-center justify-center rounded-full border-2 border-brand-gold text-brand-gold bg-white transition-all duration-300 cursor-pointer ${current === areas.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
